import React from 'react';
import { motion } from 'framer-motion';

const About = () => {
  const timelineData = [
    {
      year: '2020',
      title: 'Started Learning',
      description: 'Began my coding journey with HTML, CSS, and JavaScript. Built my first simple websites and discovered the joy of creating things with code.',
      impact: 'Gained fundamental web development skills'
    },
    {
      year: '2021',
      title: 'First Real Projects',
      description: 'Created my first full-stack applications using React and Node.js. Learned about databases, APIs, and deployment.',
      impact: 'Built confidence in full-stack development'
    },
    {
      year: '2022',
      title: 'Continuous Learning',
      description: 'Focused on modern tools and best practices. Started contributing to open-source and building projects for real users.',
      impact: 'Developed problem-solving skills and work ethic'
    },
    {
      year: '2023',
      title: 'Growing Developer',
      description: 'Working on increasingly complex projects, learning new technologies, and improving code quality. Always curious and eager to learn.',
      impact: 'Building a solid foundation for a development career'
    }
  ];

  return (
    <section id="about" className="relative py-20 px-4 overflow-hidden">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <motion.h2 className="section-title gradient-text mb-6">
            My Journey as a Developer
          </motion.h2>
          <motion.p className="section-description">
            An early-career developer building this portfolio one project at a time.
            I learn from real bugs, user feedback, and every feature I ship.
          </motion.p>
        </motion.div>

        {/* Personal Story */}
        <div className="grid grid-cols md:grid-cols-2 gap-2 md:gap-6 mb-20">
          <motion.div
            initial={{ opacity: 0}}
            whileInView={{ opacity: 1}}
            viewport={{ once: true }}
            className="glass card p-8 rounded-xl border border-purple-500/20"
          >
            <h3 className="text-2xl font-bold text-white mb-6">The Beginning</h3>
            <p className="text-gray-300 mb-4">
              I started coding by rebuilding simple web pages and reading documentation line by line.
              Those first projects were basic, but they taught me how to stay focused when things broke.
            </p>
            <p className="text-gray-300 mb-4">
              I still rely on tutorials, docs, and debugging tools, but I now also practice writing cleaner
              components and improving the experience for users.
            </p>
            <p className="text-gray-300">
              Every project is another chance to get better, and I enjoy turning ideas into usable interfaces.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="glass card p-8 rounded-xl border border-purple-500/20"
          >
            <h3 className="text-2xl font-bold text-white mb-6">What Drives Me</h3>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-purple-400 rounded-full mt-2 flex-shrink-0"></div>
                <p className="text-gray-300">
                  <strong className="text-white">Problem Solving:</strong> I enjoy breaking down complex problems,
                  even when the solution path is not obvious.
                </p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-blue-400 rounded-full mt-2 flex-shrink-0"></div>
                <p className="text-gray-300">
                  <strong className="text-white">Continuous Learning:</strong> Technology changes fast,
                  so I spend time improving my skills and understanding new tools.
                </p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <p className="text-gray-300">
                  <strong className="text-white">Building Useful Things:</strong> I focus on features
                  that feel helpful and avoid overcomplicating the experience.
                </p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-yellow-400 rounded-full mt-2 flex-shrink-0"></div>
                <p className="text-gray-300">
                  <strong className="text-white">Collaboration:</strong> I learn from teammates, code reviews,
                  and shared ideas.
                </p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Timeline */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-16"
        >
          <h3 className="text-2xl font-bold text-white text-center mb-12">Learning Timeline</h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {timelineData.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="glass card p-6 rounded-xl border border-purple-500/20 hover:border-purple-400/50 transition-all duration-300"
              >
                <div className="text-2xl font-bold gradient-text mb-3">{item.year}</div>
                <h4 className="text-lg font-semibold text-white mb-2">{item.title}</h4>
                <p className="text-gray-300 text-sm mb-3">{item.description}</p>
                <div className="text-xs text-purple-300">
                  <strong>Key Learning:</strong> {item.impact}
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Current Focus */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center"
        >
          <div className="glass card p-8 rounded-xl border border-purple-500/20 max-w-2xl mx-auto">
            <h3 className="text-xl font-bold text-white mb-4">Currently Building</h3>
            <p className="text-gray-300 mb-6">
              I'm refining this portfolio and building interactive React apps with a focus on
              clean components, responsive layouts, and practical UI behavior.
            </p>
            <div className="flex flex-wrap justify-center gap-2">
              {['React', 'Node.js', 'TypeScript', 'Tailwind CSS', 'Git'].map((tech, index) => (
                <span key={index} className="px-3 py-1 bg-purple-500/20 text-purple-300 rounded-full text-sm">
                  {tech}
                </span>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default About;
